import { useState } from 'react';
import { api } from '../api';
import type { ManagedUser, Project } from '../api';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { Empty, Loading, SortHeader, useSort, when } from '../components/bits';
import { TagSelect } from '../components/TagSelect';

/** Who has an account, what they are, and which projects they see.
 *
 *  There is no password here and none to reset: an account is an e-mail and a
 *  name, and the person signs in by asking for a link like everybody else. So
 *  adding somebody is two fields and a list of projects. Projects, not clients:
 *  somebody brought in for one project has no business reading the other four
 *  that client has had. */
export function People() {
  const { t, user } = useSession();
  const [query, setQuery] = useState('');
  const [showGone, setShowGone] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  const projects = useData(() => api.projects({}), []);
  const people = useData(
    () => api.users({ q: query || undefined, includeInactive: showGone }),
    [query, showGone],
  );

  const all = projects.data ?? [];

  const { sorted, header } = useSort(people.data, {
    name: { value: (p) => p.name },
    email: { value: (p) => p.email },
    // Administrators, then the team, then clients: the order of who can do
    // the most.
    role: { value: (p) => (p.isAdmin ? 0 : p.isStaff ? 1 : 2) },
    // The team sees everything, so it counts as more than any client.
    projects: { value: (p) => (p.isStaff ? Infinity : p.projectIds.length), first: 'desc' },
    lastSeen: { value: (p) => p.lastSeenAt, first: 'desc' },
  });

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{people.data?.length ?? 0}</p>
          <h1>{t.users}</h1>
        </div>
        <button className="primary" onClick={() => setAdding((v) => !v)}>
          {adding ? t.cancel : `${t.newUser} >`}
        </button>
      </div>

      {adding && (
        <AddSomeone
          projects={all}
          canMakeStaff={user?.isAdmin ?? false}
          onDone={() => { setAdding(false); people.reload(); }}
        />
      )}

      <div className="row" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ maxWidth: 260 }}>
          <label htmlFor="q">{t.search}</label>
          <input id="q" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <label style={{ textTransform: 'none', letterSpacing: 0, alignSelf: 'end', marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={showGone}
            onChange={(e) => setShowGone(e.target.checked)}
            style={{ width: 'auto', marginRight: 8 }}
          />
          {t.showInactive}
        </label>
      </div>

      {people.loading && !people.data ? <Loading /> : null}
      {people.data?.length === 0 ? <Empty>{t.noProjects}</Empty> : null}

      {people.data && people.data.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader {...header('name')}>{t.name}</SortHeader>
              <SortHeader {...header('email')}>{t.email}</SortHeader>
              <SortHeader {...header('role')}>{t.role}</SortHeader>
              <SortHeader {...header('projects')}>{t.projects}</SortHeader>
              <SortHeader {...header('lastSeen')}>{t.lastSeen}</SortHeader>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                projects={all}
                editing={editing === person.id}
                canManageStaff={user?.isAdmin ?? false}
                isSelf={person.id === user?.id}
                onEdit={() => setEditing(person.id)}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); people.reload(); }}
              />
            ))}
          </tbody>
        </table>
      )}
    </Shell>
  );
}

function PersonRow({
  person, projects, editing, canManageStaff, isSelf, onEdit, onClose, onSaved,
}: {
  person: ManagedUser;
  projects: Project[];
  editing: boolean;
  canManageStaff: boolean;
  isSelf: boolean;
  onEdit: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, locale } = useSession();

  if (editing) {
    return (
      <tr>
        <td colSpan={6}>
          <EditPerson
            person={person}
            projects={projects}
            canManageStaff={canManageStaff}
            isSelf={isSelf}
            onCancel={onClose}
            onSaved={onSaved}
          />
        </td>
      </tr>
    );
  }

  const role = person.isAdmin ? t.administrator : person.isStaff ? t.team : t.client;
  // Named by client, counted by project: "Brokerslink (2)" is what somebody
  // needs to know at a glance, and the project names are sixty characters
  // each.
  const perClient = new Map<string, number>();
  for (const id of person.projectIds) {
    const p = projects.find((candidate) => candidate.id === id);
    if (p) perClient.set(p.workspaceName, (perClient.get(p.workspaceName) ?? 0) + 1);
  }
  const belongs = [...perClient.entries()]
    .map(([name, n]) => (n > 1 ? `${name} (${n})` : name))
    .join(', ');

  return (
    <tr style={person.isActive ? undefined : { opacity: 0.5 }}>
      <td>{person.name}{!person.isActive && ` · ${t.inactive}`}</td>
      <td className="muted">{person.email}</td>
      <td className="muted">{role}{person.jobTitle ? ` · ${person.jobTitle}` : ''}</td>
      <td className="muted">{person.isStaff ? t.all : belongs || t.none}</td>
      <td className="muted">{person.lastSeenAt ? when(person.lastSeenAt, locale) : t.never}</td>
      <td className="num"><button onClick={onEdit}>{t.edit}</button></td>
    </tr>
  );
}

function AddSomeone({
  projects, canMakeStaff, onDone,
}: {
  projects: Project[];
  canMakeStaff: boolean;
  onDone: () => void;
}) {
  const { t } = useSession();
  const [form, setForm] = useState({ email: '', name: '', jobTitle: '', isStaff: false });
  const [chosen, setChosen] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api.createUser({ ...form, projectIds: chosen });
      onDone();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card stack" style={{ marginBottom: 'var(--sp-5)' }}>
      <div className="row">
        <div style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="new-name">{t.name}</label>
          <input id="new-name" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="new-email">{t.email}</label>
          <input id="new-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor="new-job">{t.jobTitle}</label>
          <input id="new-job" value={form.jobTitle}
            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </div>
      </div>

      {canMakeStaff && (
        <label style={{ textTransform: 'none', letterSpacing: 0 }}>
          <input type="checkbox" checked={form.isStaff}
            onChange={(e) => setForm({ ...form, isStaff: e.target.checked })}
            style={{ width: 'auto', marginRight: 8 }} />
          {t.team}
        </label>
      )}

      {!form.isStaff && (
        <ProjectPicker projects={projects} chosen={chosen} onChange={setChosen} />
      )}

      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>{t.invite} &gt;</button>
      </div>
    </form>
  );
}

function EditPerson({
  person, projects, canManageStaff, isSelf, onCancel, onSaved,
}: {
  person: ManagedUser;
  projects: Project[];
  canManageStaff: boolean;
  isSelf: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useSession();
  const [form, setForm] = useState({
    name: person.name,
    jobTitle: person.jobTitle ?? '',
    isStaff: person.isStaff,
    isAdmin: person.isAdmin,
    isActive: person.isActive,
  });
  const [chosen, setChosen] = useState<number[]>(person.projectIds);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api.updateUser(person.id, {
        name: form.name,
        jobTitle: form.jobTitle,
        isActive: form.isActive,
        ...(canManageStaff ? { isStaff: form.isStaff, isAdmin: form.isAdmin } : {}),
        projectIds: form.isStaff ? [] : chosen,
      });
      onSaved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card stack">
      <div className="row">
        <div style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor={`n-${person.id}`}>{t.name}</label>
          <input id={`n-${person.id}`} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor={`j-${person.id}`}>{t.jobTitle}</label>
          <input id={`j-${person.id}`} value={form.jobTitle}
            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </div>
      </div>

      <div className="row">
        {canManageStaff && (
          <>
            <label style={{ textTransform: 'none', letterSpacing: 0 }}>
              <input type="checkbox" checked={form.isStaff}
                onChange={(e) => setForm({ ...form, isStaff: e.target.checked })}
                style={{ width: 'auto', marginRight: 8 }} />
              {t.team}
            </label>
            <label style={{ textTransform: 'none', letterSpacing: 0 }}>
              <input type="checkbox" checked={form.isAdmin}
                onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })}
                style={{ width: 'auto', marginRight: 8 }} />
              {t.administrator}
            </label>
          </>
        )}
        {/* Deactivating yourself would lock you out, so it is not offered. */}
        {!isSelf && (
          <label style={{ textTransform: 'none', letterSpacing: 0 }}>
            <input type="checkbox" checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              style={{ width: 'auto', marginRight: 8 }} />
            {t.active}
          </label>
        )}
      </div>

      {!form.isStaff && (
        <ProjectPicker projects={projects} chosen={chosen} onChange={setChosen} />
      )}

      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>{t.save} &gt;</button>
        <button type="button" onClick={onCancel}>{t.cancel}</button>
      </div>
    </form>
  );
}

/** Which projects somebody sees, which is also which clients they see: a
 *  client opens because one of its projects does. Staff see everything, so the
 *  picker only appears for everybody else. */
function ProjectPicker({
  projects, chosen, onChange,
}: {
  projects: Project[];
  chosen: number[];
  onChange: (next: number[]) => void;
}) {
  const { t } = useSession();
  return (
    <div>
      <label htmlFor="ws-picker">{t.projects}</label>
      <TagSelect
        id="ws-picker"
        multiple
        value={chosen}
        onChange={onChange}
        placeholder={t.search}
        options={projects.map((p) => ({ value: p.id, label: p.title, hint: p.workspaceName }))}
      />
    </div>
  );
}
